import Order from './order';
import Element from '../element/element';
import RTSControls from '../control/rtsControls';
import MoveToOrder from './moveToOrder';

import {
  getAngleBy2XY,
  getDistanceBy2XY,
  CoordsType,
  getDiffBetween2Angles,
} from '../../utils/geometry';
import { avg } from '../../utils/math';
import { Faction } from '../../utils/faction';


export interface AttackOrderParams {
  target: Element;
  softMode?: boolean;
}

export default class AttackOrder extends Order implements AttackOrderParams {
  target: Element;
  softMode: boolean;

  distanceToReach!: number;
  originCoords!: CoordsType;
  maxPursuitRange? = 500;

  constructor(params: AttackOrderParams) {
    super();
    this.target = params.target;
    this.softMode = params.softMode ?? false;
    this.color = RTSControls.colors.hoverEnemy;
  }

  getEndCoords(): CoordsType {
    return {
      x: this.target.x,
      y: this.target.y,
    };
  }

  update(modifier: number): AttackOrder {
    const element = this.getElement();
    const {
      target,
    } = this;

    const {
      x,
      y,
    } = element;

    const distanceToObjective = getDistanceBy2XY({
      x1: this.behaviour.element.x,
      y1: this.behaviour.element.y,
      x2: target.x,
      y2: target.y,
    });
    const destinationAngle = getAngleBy2XY({
      x1: x,
      y1: y,
      x2: target.x,
      y2: target.y,
    });
    if (getDiffBetween2Angles(element.lookAngle, destinationAngle) > 1) // fixing a bug that caused slow turning ships to "shake"
      element.moveAngle = element.rotateTowardsAngle(destinationAngle, modifier);

    if (distanceToObjective <= element.maxFiringRange * 0.75)
      element.brake(modifier);
    else
      element.accelerate(modifier);

    /* fills the alert gauge */
    if (this.getElement().faction === Faction.ENEMY && this.getElement().scene.alertGauge) {
      this.getElement().scene.alertGauge.addProgress(1 * modifier);
    }

    return this;
  }

  onComplete(): void {
    this.getElement().stop();
    this.getElement()?.weapons.forEach((w) => {
      w.prioritytarget = undefined;
    });
  }

  onStart(): void {
    const element = this.getElement();
    const {
      target,
    } = this;

    /* set target as priorityTarget for every available weapon */
    element?.weapons.forEach((w) => {
      w.prioritytarget = target;
    });

    /* save the coords of the ship when the order was given */
    const { x, y } = element;
    this.originCoords = {
      x: x,
      y: y,
    };
  }

  isComplete(): boolean {
    /* on soft mode, target can be switched according to various conditions (distance, another target closer, ...) */
    if (this.softMode) {
      if (this.maxPursuitRange) {
        /* Stop attacking if wanders too far */
        const distanceFromOrigin = getDistanceBy2XY({
          x1: this.originCoords.x,
          y1: this.originCoords.y,
          x2: this.getElement().x,
          y2: this.getElement().y,
        });
        if (distanceFromOrigin > this.maxPursuitRange) {
          if (this.behaviour.orders.length === 1) // nothing else to do: go back to original coords
          {
            this.behaviour.setOrder(new MoveToOrder({
              x: this.originCoords.x,
              y: this.originCoords.y,
            }));
          }
          return true;
        }
      }

      /* change target if another one is closer */
      const nearestEnemy = this.getElement().getNearestEnemy();
      return (nearestEnemy !== this.target);
    }

    return this.getElements()[this.target.id] === undefined; // TARGET DESTROYED!
  }

  isPossible(): boolean {
    const element = this.getElement();
    const {
      target,
    } = this;

    const averageFiringRange = avg(element.weapons.map(w => w.range));
    const distanceToObjective = getDistanceBy2XY({
      x1: this.behaviour.element.x,
      y1: this.behaviour.element.y,
      x2: target.x,
      y2: target.y,
    });

    return (element?.moveSpeedMax > 0 || distanceToObjective < averageFiringRange);
  }
}
